/**
 * Impersonate Hooks
 *
 * React Query hooks para funcionalidade de impersonate (acessar como).
 *
 * @since 2025-01-10
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { impersonateApi } from '@/services/impersonateApi';
import { useImpersonateStore } from '@/store/impersonateStore';
import { notify } from '@/store/uiStore';

// ==========================================
// QUERY KEYS
// ==========================================

export const impersonateKeys = {
  all: ['impersonate'] as const,
  info: () => [...impersonateKeys.all, 'info'] as const,
};

// ==========================================
// HOOKS
// ==========================================

/**
 * Hook para obter informações do impersonate atual
 */
export function useImpersonateInfo() {
  return useQuery({
    queryKey: impersonateKeys.info(),
    queryFn: () => impersonateApi.getImpersonateInfo(),
    staleTime: 60 * 1000, // 1 minuto
    gcTime: 5 * 60 * 1000, // 5 minutos
    retry: false,
  });
}

/**
 * Hook para iniciar impersonate
 *
 * Após sucesso: limpa TODO o cache do TanStack Query e recarrega a página.
 * Reload total é necessário porque a invalidação parcial deixava queries
 * de outros módulos (Service Desk, products, dashboards) servindo dados
 * do usuário anterior — vazamento de dados entre escopos.
 *
 * O token novo do impersonate já foi persistido em localStorage por
 * impersonateApi.startImpersonate ANTES do onSuccess, então o reload
 * mantém o usuário autenticado no novo escopo.
 */
export function useStartImpersonate() {
  const queryClient = useQueryClient();
  const { setImpersonating } = useImpersonateStore();

  return useMutation({
    mutationFn: (targetPersonId: number) => impersonateApi.startImpersonate(targetPersonId),
    onSuccess: (response) => {
      if (response.success) {
        // Atualizar store (zustand) — mesmo será resetado pelo reload, mas
        // mantemos pra consistência caso o reload falhe/seja interceptado.
        setImpersonating({
          originalUser: response.originalUser,
          impersonatedUser: response.impersonatedUser,
        });

        // Notificar antes do reload (toast pode persistir via storage event listener)
        notify.success(
          'Impersonate iniciado',
          `Você está acessando como ${response.impersonatedUser.name}`
        );

        // Limpar TODAS as queries do TanStack Query — garante que nenhuma
        // query de outro módulo (Service Desk, products, etc.) sirva dados
        // do usuário anterior.
        queryClient.clear();

        // Reset total de stores Zustand/Context via reload + redirect pra home.
        // Token já está em localStorage, então o reload mantém autenticação.
        window.location.assign('/');
      }
    },
    onError: (error: Error) => {
      notify.error('Erro ao iniciar impersonate', error.message);
    },
  });
}

/**
 * Hook para sair do impersonate
 *
 * Após sucesso: limpa TODO o cache do TanStack Query e recarrega a página.
 * Mesma justificativa de useStartImpersonate.
 */
export function useExitImpersonate() {
  const queryClient = useQueryClient();
  const { clearImpersonate } = useImpersonateStore();

  return useMutation({
    mutationFn: () => impersonateApi.exitImpersonate(),
    onSuccess: (response) => {
      if (response.success) {
        // Limpar store (idem useStartImpersonate)
        clearImpersonate();

        // Notificar antes do reload
        notify.success('Impersonate encerrado', response.message);

        // Limpar TODAS as queries do TanStack Query
        queryClient.clear();

        // Reset total + redirect pra home. Token original já foi restaurado
        // em localStorage por impersonateApi.exitImpersonate.
        window.location.assign('/');
      }
    },
    onError: (error: Error) => {
      notify.error('Erro ao sair do impersonate', error.message);
      // Em caso de erro, limpar dados locais para evitar estado inconsistente
      impersonateApi.clearImpersonateData();
      clearImpersonate();
    },
  });
}

/**
 * Hook principal de impersonate
 *
 * Combina estado e ações em um único hook
 */
export function useImpersonate() {
  const { isImpersonating, originalUser, impersonatedUser, startedAt } = useImpersonateStore();
  const startMutation = useStartImpersonate();
  const exitMutation = useExitImpersonate();

  const startImpersonate = useCallback(
    async (targetPersonId: number) => {
      return startMutation.mutateAsync(targetPersonId);
    },
    [startMutation]
  );

  const exitImpersonate = useCallback(async () => {
    return exitMutation.mutateAsync();
  }, [exitMutation]);

  return {
    // State
    isImpersonating,
    originalUser,
    impersonatedUser,
    startedAt,

    // Actions
    startImpersonate,
    exitImpersonate,

    // Loading states
    isStarting: startMutation.isPending,
    isExiting: exitMutation.isPending,
    isLoading: startMutation.isPending || exitMutation.isPending,

    // Error states
    startError: startMutation.error,
    exitError: exitMutation.error,
  };
}

/**
 * Hook para verificar se pode impersonar
 *
 * Verifica se o usuário tem permissão de impersonate
 */
export function useCanImpersonate(userRoles: string[] = []) {
  const allowedRoles = new Set([
    'admin',
    'superadmin',
    'ADMIN',
    'SUPERADMIN',
    'administrator',
    'Sadin',
    'sadin',
  ]);

  const canImpersonate = userRoles.some((role) => allowedRoles.has(role));

  return { canImpersonate };
}

// ==========================================
// EXPORTS
// ==========================================

export default useImpersonate;
